import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandLogo } from "@/components/BrandLogo";
import { ForgotPasswordDialog } from "@/components/ForgotPasswordDialog";

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", garage_name: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form);
      }
      toast.success("Connexion réussie");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const demoGarage = async () => {
    setLoading(true);
    try {
      await login("demo@mecapiece.fr", "demo123");
      toast.success("Connecté avec le compte démo garagiste");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left visual */}
      <div className="hidden lg:flex relative flex-col justify-between p-12 bg-gradient-to-br from-[#0033CC] via-[#0029A3] to-[#001A66] text-white overflow-hidden">
        <div className="absolute top-0 left-0 right-0 tricolor-bar z-20" />
        <div className="absolute inset-0 hero-grid opacity-40" />
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-red-600/20 blur-3xl" />
        <div className="relative z-10">
          <BrandLogo size={88} textClass="text-3xl [&_.text-metal]:bg-none [&_.text-metal]:text-white [&_.text-brand-red]:text-red-400" testId="auth-brand-logo" />
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-heading text-5xl font-extrabold leading-tight">
            Sourcez la bonne pièce,<br /><span className="text-red-400">au bon délai.</span>
          </h1>
          <p className="text-blue-100 text-lg max-w-md">
            Décodez un véhicule par plaque ou numéro de châssis (VIN), identifiez la pièce et fixez l'urgence — Urgent, En stock ou délai de 1 à 7 jours.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            {["Décodage VIN officiel", "Suivi des demandes", "Espace fournisseur"].map((t) => (
              <span key={t} className="px-3 py-1.5 rounded-full bg-white/10 text-sm border border-white/10">{t}</span>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-blue-200 text-sm">© 2026 Méca Pièces PRO — Outil professionnel de sourcing pièces auto</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md" data-testid="auth-container"
        >
          <div className="lg:hidden mb-8">
            <BrandLogo size={56} textClass="text-xl" testId="auth-brand-logo-mobile" />
          </div>

          <h2 className="font-heading text-3xl font-extrabold tracking-tight">
            {mode === "login" ? "Connexion" : "Créer un compte"}
          </h2>
          <p className="text-muted-foreground mt-1 mb-6">
            {mode === "login" ? "Accédez à votre espace garagiste." : "Rejoignez la plateforme professionnelle."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label htmlFor="name">Nom complet</Label>
                  <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")} required placeholder="Jean Dupont" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="garage">Nom du garage / enseigne</Label>
                  <Input id="garage" data-testid="register-garage" value={form.garage_name} onChange={set("garage_name")} placeholder="Garage du Centre" className="mt-1.5" />
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="auth-email" type="email" value={form.email} onChange={set("email")} required placeholder="vous@garage.fr" className="mt-1.5" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Mot de passe</Label>
                {mode === "login" && <button type="button" data-testid="forgot-password-link" onClick={() => setForgotOpen(true)} className="text-xs text-blue-700 font-semibold hover:underline">Mot de passe oublié ?</button>}
              </div>
              <Input id="password" data-testid="auth-password" type="password" value={form.password} onChange={set("password")} required placeholder="••••••••" className="mt-1.5" />
            </div>

            <Button type="submit" data-testid="auth-submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800 h-11 text-base">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </Button>
          </form>

          <Button variant="outline" onClick={demoGarage} disabled={loading} data-testid="demo-garage-btn" className="w-full mt-3 h-11 border-blue-500/40 text-blue-700 hover:bg-blue-500/5">
            <Building2 className="h-4 w-4 mr-2" /> Démo garagiste (1 clic)
          </Button>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {mode === "login" ? "Pas encore de compte ?" : "Déjà inscrit ?"}{" "}
            <button data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-blue-700 font-semibold hover:underline">
              {mode === "login" ? "Créer un compte" : "Se connecter"}
            </button>
          </p>
        </motion.div>
      </div>
      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />
    </div>
  );
}
